import { useState } from "react";
import { FileText, X, Loader2, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "@/lib/api";

const ORIGIN = API.replace(/\/api$/, "");
export const attUrl = (a) => (a.url?.startsWith("http") ? a.url : `${ORIGIN}${a.url}`);
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);

/** Renders a message's attachments: images as a moodboard grid (lightbox on click), PDFs as file cards. */
export default function DMAttachments({ attachments, mine }) {
  const [open, setOpen] = useState(null);
  const images = attachments.filter((a) => a.kind === "image");
  const files = attachments.filter((a) => a.kind !== "image");
  const cols = images.length === 1 ? "grid-cols-1" : images.length <= 4 ? "grid-cols-2" : "grid-cols-3";
  return (
    <div className="space-y-2" data-testid="dm-attachments">
      {images.length > 0 && (
        <div className={`grid ${cols} gap-1.5 ${images.length === 1 ? "max-w-[300px]" : "w-[260px] sm:w-[320px]"}`}>
          {images.map((a, i) => (
            <button key={a.file_id} type="button" onClick={() => setOpen(i)} className={`relative overflow-hidden rounded-xl bg-black/10 ${images.length === 1 ? "" : "aspect-square"} ${images.length === 3 && i === 0 ? "col-span-2 aspect-[2/1]" : ""}`} data-testid={`dm-att-image-${a.file_id}`}>
              <img src={attUrl(a)} alt={a.filename} loading="lazy" className={`w-full ${images.length === 1 ? "h-auto max-h-[360px] object-contain" : "h-full object-cover"} hover:opacity-90 transition-opacity`} />
            </button>
          ))}
        </div>
      )}
      {files.map((a) => (
        <a key={a.file_id} href={attUrl(a)} target="_blank" rel="noreferrer" className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${mine ? "bg-white/10 hover:bg-white/15" : "bg-[#FAF3EA] hover:bg-[#F3E7D6]"} transition-colors`} data-testid={`dm-att-file-${a.file_id}`}>
          <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${mine ? "bg-white/15" : "bg-white"}`}><FileText className="w-4 h-4 text-[#C68E59]" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-medium truncate">{a.filename}</span>
            <span className={`block text-[10px] ${mine ? "text-white/60" : "text-[#8B8680]"}`}>Moodboard PDF · {fmtSize(a.size || 0)}</span>
          </span>
          <Download className="w-3.5 h-3.5 opacity-70" />
        </a>
      ))}
      {open != null && <Lightbox images={images} index={open} onClose={() => setOpen(null)} onIndex={setOpen} />}
    </div>
  );
}

function Lightbox({ images, index, onClose, onIndex }) {
  const a = images[index];
  return (
    <div className="fixed inset-0 z-[90] bg-[#0F0E0D]/95 flex items-center justify-center p-4" onClick={onClose} data-testid="dm-lightbox">
      <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" onClick={onClose} aria-label="Fermer" data-testid="dm-lightbox-close"><X className="w-5 h-5" /></button>
      {images.length > 1 && (
        <>
          <button className="absolute left-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" onClick={(e) => { e.stopPropagation(); onIndex((index - 1 + images.length) % images.length); }} aria-label="Précédent"><ChevronLeft className="w-5 h-5" /></button>
          <button className="absolute right-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" onClick={(e) => { e.stopPropagation(); onIndex((index + 1) % images.length); }} aria-label="Suivant"><ChevronRight className="w-5 h-5" /></button>
        </>
      )}
      <img src={attUrl(a)} alt={a.filename} className="max-w-full max-h-[88vh] object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
      <a href={attUrl(a)} download target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="absolute bottom-5 inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs px-4 h-9"><Download className="w-3.5 h-3.5" /> Télécharger</a>
    </div>
  );
}

/** Composer strip: thumbnails of uploaded-but-unsent attachments. */
export function PendingAttachments({ items, uploading, onRemove }) {
  if (!items.length && !uploading) return null;
  return (
    <div className="flex gap-2 flex-wrap mb-2.5" data-testid="dm-pending-attachments">
      {items.map((a) => (
        <div key={a.file_id} className="relative w-16 h-16 rounded-xl overflow-hidden border border-[#EADDC9] bg-[#FAF3EA]" data-testid={`dm-pending-${a.file_id}`}>
          {a.kind === "image" ? <img src={attUrl(a)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex flex-col items-center justify-center text-[#A26F3E]"><FileText className="w-5 h-5" /><span className="text-[9px] mt-1">PDF</span></div>}
          <button type="button" onClick={() => onRemove(a.file_id)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-[#1A1A1A]/80 text-white flex items-center justify-center hover:bg-[#D32F2F]" aria-label="Retirer" data-testid={`dm-pending-remove-${a.file_id}`}><X className="w-3 h-3" /></button>
        </div>
      ))}
      {Array.from({ length: uploading }).map((_, i) => (
        <div key={`up-${i}`} className="w-16 h-16 rounded-xl border border-dashed border-[#C68E59] flex items-center justify-center" data-testid="dm-pending-uploading"><Loader2 className="w-4 h-4 text-[#C68E59] animate-spin" /></div>
      ))}
    </div>
  );
}
